'use client';

import { useTranslations } from 'next-intl';
import { type DependencyList, useEffect, useRef } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import type { TempUploadItem, TempUploadRefs } from '@/app/types';
import { registerTempUploadRetryHandler } from '@/app/utils/client';

export function toolTempUploadKey(type: string, ...parts: string[]): string {
  return ['tool-temp', type, ...parts].join(':');
}

export function useRunTempUploadBatch(
  userInfo: unknown,
  logLabel: string,
  getItems: (refs: TempUploadRefs) => TempUploadItem[],
  deps: DependencyList,
): void {
  const t = useTranslations();
  const processedRef = useRef(new Set<string>());
  const pendingRef = useRef(new Set<string>());
  const failedRef = useRef(new Set<string>());
  const getItemsRef = useRef(getItems);
  getItemsRef.current = getItems;

  useEffect(() => {
    const retry = async () => {
      const failed = Array.from(failedRef.current);
      let success = true;
      let retried = false;
      const replacements = [];
      for (const uploadKey of failed) {
        processedRef.current.delete(uploadKey);
        pendingRef.current.delete(uploadKey);
        const item = getItemsRef
          .current({ processed: processedRef, pending: pendingRef })
          .find((candidate) => candidate.uploadKey === uploadKey);
        if (!item) continue;
        retried = true;
        failedRef.current.delete(uploadKey);
        pendingRef.current.add(uploadKey);
        try {
          replacements.push(...((await item.run()) ?? []));
          processedRef.current.add(uploadKey);
        } catch (error) {
          failedRef.current.add(uploadKey);
          processedRef.current.add(uploadKey);
          toast({ type: 'error', message: t('popup.toast.uploadingError') });
          console.error(`${logLabel}:`, uploadKey, error);
          success = false;
        } finally {
          pendingRef.current.delete(uploadKey);
        }
      }
      return { success, replacements, retried };
    };
    return registerTempUploadRetryHandler(retry);
  }, [logLabel, t]);

  useEffect(() => {
    if (!userInfo) return;
    const refs = { processed: processedRef, pending: pendingRef };
    getItems(refs).forEach(({ uploadKey, run }) => {
      if (processedRef.current.has(uploadKey) || pendingRef.current.has(uploadKey)) return;
      pendingRef.current.add(uploadKey);
      run()
        .then(() => processedRef.current.add(uploadKey))
        .catch((error) => {
          failedRef.current.add(uploadKey);
          processedRef.current.add(uploadKey);
          toast({ type: 'error', message: t('popup.toast.uploadingError') });
          console.error(`${logLabel}:`, uploadKey, error);
        })
        .finally(() => pendingRef.current.delete(uploadKey));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caller controls dependency list
  }, deps);
}
